import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_LANDING_SECTION_ID, LANDING_SPY_SECTION_IDS } from './landingNavItems';

const getActiveSectionId = (sections: HTMLElement[]) => {
  const header = document.querySelector('header');
  const marker = (header?.getBoundingClientRect().bottom ?? 0) + 1;
  let activeId = sections[0]?.id ?? DEFAULT_LANDING_SECTION_ID;

  sections.forEach((section) => {
    if (section.getBoundingClientRect().top <= marker) {
      activeId = section.id;
    }
  });

  return activeId;
};

export function useLandingSectionSpy() {
  const [activeSectionId, setActiveSectionId] = useState(DEFAULT_LANDING_SECTION_ID);

  useEffect(() => {
    const sections = LANDING_SPY_SECTION_IDS.map((id) => document.getElementById(id)).filter(
      (section): section is HTMLElement => section !== null
    );

    if (sections.length === 0) {
      return;
    }

    let frame = 0;

    const update = () => {
      const nextActiveId = getActiveSectionId(sections);
      setActiveSectionId((current) => (current === nextActiveId ? current : nextActiveId));
    };

    const requestUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (!section) {
      return;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSectionId(sectionId);
  }, []);

  return { activeSectionId, scrollToSection };
}
